import { toast } from "sonner";
import type { UseFormSetError } from "react-hook-form";
import { apiErrorMessage, getActiveLocale, translator } from "@/i18n";
import { ApiClientError } from "./api";

/**
 * One place that decides how a mutation failure reaches the user, so no screen invents
 * its own convention.
 *
 * A validation failure renders **field-level** errors and does not toast: a toast is
 * never the only record of a validation problem, because it disappears while the
 * offending field stays wrong. Everything else toasts, and carries the diagnostic
 * identifier so a support conversation starts from a reference.
 *
 * These are called from `onError` callbacks rather than from components, so they cannot use
 * `useT()`. They build a translator from the active locale instead — the same value
 * `LocaleProvider` published for `lib/format.ts`, and for the same reason.
 */
export function reportMutationError(
  error: unknown,
  options: {
    /** react-hook-form's setter, when the mutation came from a form. */
    setError?: UseFormSetError<never>;
    fallback?: string;
  } = {},
): void {
  const t = translator(getActiveLocale());
  const { setError, fallback = t("toast.genericFailure") } = options;

  if (error instanceof ApiClientError) {
    if (error.isValidation && setError) {
      for (const [field, messages] of Object.entries(error.fieldErrors)) {
        const message = messages[0];
        if (message) setError(field as never, { message });
      }
      return;
    }

    // A stale write is not a fault to apologise for; it is information the user has to
    // act on, so it is worded as a decision rather than an error.
    if (error.isStaleVersion) {
      // No "reload" instruction: the product has no refresh affordance, and the caller has
      // already re-read what the server holds. What the user has to do is look and repeat.
      toast.error(t("toast.staleTitle"), { description: t("toast.staleDescription") });
      return;
    }

    // Translated by code where the client has better words, and the server's own message
    // where that message carries the specifics. See `i18n/errorMessage.ts`.
    toast.error(apiErrorMessage(t, error, fallback), { description: t("toast.reference", error.requestId) });
    return;
  }

  toast.error(fallback);
}

export function reportMutationSuccess(message: string): void {
  toast.success(message);
}
